import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Target, BarChart3, Globe, Network } from "lucide-react";
import BrandMarquee from "@/components/BrandMarquee";
import FAQSection from "@/components/FAQSection";
import CTASection from "@/components/CTASection";
import SectionLabel from "@/components/SectionLabel";
import AnimatedCounter from "@/components/AnimatedCounter";
import { HOME_FAQS, INDUSTRIES, IMAGES } from "@/data/content";

const heroWords = ["Powering", "Brands", "Through", "Strategic Influence"];

const FEATURES = [
  {
    Icon: Target,
    title: "Strategic Influencer Discovery",
    body: "Identify and partner with verified creators across niches, languages, and regions — perfectly aligned to your audience.",
    span: "md:col-span-7",
  },
  {
    Icon: BarChart3,
    title: "Real-time Insights",
    body: "Track engagement, reach and campaign ROI through transparent analytics.",
    span: "md:col-span-5",
  },
  {
    Icon: Globe,
    title: "Regional & Hyperlocal Reach",
    body: "Tap into Tamil, Telugu, Malayalam and Kannada creator communities along with pan-India coverage.",
    span: "md:col-span-5",
  },
  {
    Icon: Network,
    title: "Streamlined Campaign Coordination",
    body: "Structured workflows take you from creator selection to content delivery without the operational stress.",
    span: "md:col-span-7",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const blob1Y = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const blob2Y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroFade = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 0.94]);

  return (
    <div data-testid="home-page" className="bg-[#050505] text-[#F3E8E0]">
      {/* HERO */}
      <section ref={heroRef} className="relative pt-32 pb-24 md:pt-44 md:pb-32 overflow-hidden grain-bg">
        <motion.div style={{ y: blob1Y }} className="absolute top-20 right-0 w-[600px] h-[600px] bg-[#FF4A00] opacity-10 rounded-full blur-[180px] pointer-events-none" />
        <motion.div style={{ y: blob2Y }} className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-[#CCFF00] opacity-[0.04] rounded-full blur-[160px] pointer-events-none" />

        <motion.div style={{ opacity: heroFade, scale: heroScale }} className="max-w-7xl mx-auto px-6 md:px-10 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#222] bg-[#0a0a0a] text-xs font-medium tracking-wider uppercase text-[#A3A3A3] mb-8"
            data-testid="hero-badge"
          >
            <Sparkles className="w-3.5 h-3.5 text-[#FF4A00]" />
            Influencer Marketing, Engineered.
          </motion.div>

          <h1
            data-testid="hero-title"
            className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-[7rem] font-semibold tracking-tighter leading-[0.92] max-w-6xl"
          >
            {heroWords.map((w, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * i }}
                className={`inline-block mr-3 md:mr-5 ${w === "Strategic Influence" ? "italic text-[#FF4A00] font-medium" : ""}`}
              >
                {w}
              </motion.span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-10 text-lg md:text-xl text-[#A3A3A3] max-w-2xl leading-relaxed"
          >
            Incer is a strategy-driven influencer marketing platform that helps brands discover verified
            influencers, manage campaigns and track performance with real-time analytics.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.85 }}
            className="mt-12 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/brands"
              data-testid="hero-cta-brands"
              className="inline-flex items-center justify-center gap-2 bg-[#FF4A00] text-white text-base font-medium px-7 py-4 rounded-full hover:bg-[#E64300] transition-all active:scale-95 group"
            >
              For Brands <ArrowUpRight className="w-5 h-5 group-hover:rotate-45 transition-transform" />
            </Link>
            <Link
              to="/creators"
              data-testid="hero-cta-creators"
              className="inline-flex items-center justify-center gap-2 border border-[#333] text-[#F3E8E0] text-base font-medium px-7 py-4 rounded-full hover:border-[#FF4A00] hover:text-[#FF4A00] transition-colors"
            >
              For Creators <ArrowUpRight className="w-5 h-5" />
            </Link>
          </motion.div>

          {/* Quick stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1 }}
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8"
            data-testid="hero-stats"
          >
            {[
              { v: "20K+", l: "Verified Creators" },
              { v: "7+", l: "Languages" },
              { v: "200+", l: "Industries Served" },
              { v: "Real-time", l: "Campaign Analytics" },
            ].map((s, i) => (
              <div key={i} className="border-t border-[#222] pt-5">
                <div className="font-display text-3xl md:text-4xl font-medium tracking-tighter text-[#F3E8E0]">
                  <AnimatedCounter value={s.v} />
                </div>
                <div className="mt-1 text-sm text-[#A3A3A3]">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* CLIENT MARQUEE */}
      <section className="py-16 border-y border-[#1a1a1a] bg-[#070707]" data-testid="home-clients">
        <BrandMarquee label="Brands that trust Incer" />
      </section>

      {/* WHAT WE DO — BENTO */}
      <section className="py-24 md:py-32" data-testid="home-features">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
            <div className="max-w-2xl">
              <SectionLabel className="mb-5">What we do</SectionLabel>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter text-[#F3E8E0] leading-[0.95]">
                Influencer marketing made <span className="text-[#FF4A00] italic">smarter, scalable, strategic.</span>
              </h2>
            </div>
            <p className="lg:max-w-md text-lg text-[#A3A3A3]">
              We combine influencer discovery, campaign coordination and performance insights into one
              powerful, strategy-led platform.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {FEATURES.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className={`card-glow group relative bg-[#0c0c0c] border border-[#1a1a1a] rounded-2xl p-8 md:p-10 hover:border-[#FF4A00]/40 transition-colors ${f.span}`}
                data-testid={`feature-card-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#FF4A00]/10 flex items-center justify-center mb-8 group-hover:bg-[#FF4A00]/20 transition-colors">
                  <f.Icon className="w-6 h-6 text-[#FF4A00]" />
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight mb-3">{f.title}</h3>
                <p className="text-[#A3A3A3] leading-relaxed">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE INCER */}
      <section className="py-24 md:py-32 border-t border-[#1a1a1a] bg-[#070707]" data-testid="home-why">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <SectionLabel className="mb-5">Why Incer</SectionLabel>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[0.95]">
                Built for brands and agencies who take influence seriously.
              </h2>
              <p className="mt-8 text-lg text-[#A3A3A3] max-w-md">
                From discovery to delivery, every step is structured, transparent and measurable —
                so you can scale without losing control.
              </p>
              <Link
                to="/brands"
                data-testid="why-learn-more"
                className="mt-8 inline-flex items-center gap-2 text-[#FF4A00] font-medium hover:gap-3 transition-all"
              >
                Explore brand benefits <ArrowUpRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { t: "Verified Creators", d: "Audience-aligned, niche-relevant" },
                { t: "Regional + Hyperlocal", d: "South India to pan-India reach" },
                { t: "Structured Workflows", d: "From brief to delivery" },
                { t: "Transparent Reporting", d: "Real-time campaign insights" },
                { t: "Scalable Collaborations", d: "Long-term creator partnerships" },
                { t: "One Unified Platform", d: "No fragmented tools" },
              ].map((b, i) => (
                <div
                  key={i}
                  data-testid={`why-block-${i}`}
                  className="p-6 border border-[#1a1a1a] rounded-2xl bg-[#0a0a0a] hover:border-[#FF4A00]/40 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF4A00] mt-2.5" />
                    <div>
                      <div className="font-display text-lg font-medium">{b.t}</div>
                      <div className="text-sm text-[#A3A3A3] mt-1">{b.d}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-24 md:py-32" data-testid="home-industries">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-16">
            <SectionLabel className="justify-center inline-block mb-5">Industries we power</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[0.95] max-w-3xl mx-auto">
              Strategic influence, across categories.
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {INDUSTRIES.map((ind, i) => (
              <motion.div
                key={ind.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                data-testid={`industry-${i}`}
                className="aspect-square sm:aspect-[5/4] flex flex-col items-start justify-end p-6 border border-[#1a1a1a] rounded-2xl hover:border-[#FF4A00] hover:bg-[#FF4A00]/5 transition-all cursor-default group"
              >
                <ind.Icon className="w-6 h-6 text-[#FF4A00] mb-auto group-hover:scale-110 transition-transform" />
                <div className="font-display text-xl md:text-2xl font-medium tracking-tight">{ind.name}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection testId="home-cta" />
      <FAQSection items={HOME_FAQS} testId="home-faq" />
    </div>
  );
}
